import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Save, Loader2, RotateCcw, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  ["name", "Nome"], ["email", "E-mail"], ["phone", "Telefone"], ["city", "Cidade"], ["bio", "Bio"], ["account_type", "Tipo de conta"],
];

/** Admin: edit any user's data + hard-delete the account (frees e-mail/phone). */
export default function AdminUserEditPanel({ profile, onSaved, onDeleted }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [deleteContent, setDeleteContent] = useState(true);

  useEffect(() => {
    setForm({ name: profile.name || "", email: profile.email || "", phone: profile.phone || "", city: profile.city || "", bio: profile.bio || "",
      account_type: profile.account_type || "", is_verified: !!profile.is_verified, is_company: !!profile.is_company });
  }, [profile]);

  const dirty = FIELDS.some(([k]) => (form[k] ?? "") !== (profile[k] ?? "")) || form.is_verified !== !!profile.is_verified || form.is_company !== !!profile.is_company;

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const patch = { ...extra };
      FIELDS.forEach(([k]) => { if ((form[k] ?? "") !== (profile[k] ?? "")) patch[k] = form[k]; });
      if (form.is_verified !== !!profile.is_verified) patch.is_verified = form.is_verified;
      if (form.is_company !== !!profile.is_company) patch.is_company = form.is_company;
      const r = await axios.put(`${API}/admin/users/${profile.user_id}/edit`, patch);
      toast.success("Dados do usuário atualizados");
      onSaved?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/users/${profile.user_id}`, { data: { confirm, delete_content: deleteContent } });
      toast.success(`Conta excluída. E-mail ${r.data.email_released} liberado para novo cadastro.`);
      setDelOpen(false);
      onDeleted?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
    setBusy(false);
  };

  return (
    <Card className="p-4 space-y-3" data-testid="admin-user-edit-panel">
      <div className="flex items-center gap-2 text-sm font-bold"><Pencil className="w-4 h-4 text-primary" /> Editar dados do usuário</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={k === "bio" ? "sm:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</label>
            <Input value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="h-9" data-testid={`admin-edit-${k}`} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-2"><Switch checked={!!form.is_verified} onCheckedChange={(v) => setForm((f) => ({ ...f, is_verified: v }))} data-testid="admin-edit-verified" /> Verificado</label>
        <label className="flex items-center gap-2"><Switch checked={!!form.is_company} onCheckedChange={(v) => setForm((f) => ({ ...f, is_company: v }))} data-testid="admin-edit-company" /> Empresa</label>
        <Button size="sm" variant="ghost" onClick={() => save({ reset_picture: true })} disabled={busy} data-testid="admin-edit-reset-picture"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Resetar foto</Button>
        <Button size="sm" variant="ghost" onClick={() => save({ reset_cover: true })} disabled={busy} data-testid="admin-edit-reset-cover"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Resetar capa</Button>
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
        <Button size="sm" variant="destructive" onClick={() => { setConfirm(""); setDelOpen(true); }} disabled={busy || profile.is_super_admin} data-testid="admin-delete-user-btn">
          <Trash2 className="w-4 h-4 mr-1" /> Excluir cadastro
        </Button>
        <Button size="sm" onClick={() => save()} disabled={busy || !dirty} data-testid="admin-edit-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Salvar alterações</>}
        </Button>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-md" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Excluir cadastro definitivamente</DialogTitle>
            <DialogDescription>
              Remove a conta de <b>{profile.name}</b> ({profile.email}). O e-mail e o telefone ficam livres para um novo cadastro. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm"><Switch checked={deleteContent} onCheckedChange={setDeleteContent} data-testid="admin-delete-content-toggle" /> Apagar também posts, comentários e curtidas</label>
          <div>
            <label className="text-xs text-muted-foreground">Digite o e-mail do usuário ou <b>EXCLUIR</b> para confirmar</label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={profile.email} className="mt-1" data-testid="admin-delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)} data-testid="admin-delete-cancel">Cancelar</Button>
            <Button variant="destructive" onClick={remove} disabled={busy || !confirm.trim()} data-testid="admin-delete-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Excluir definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
